import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowDown } from 'lucide-react'
import { spring } from '@/lib/motion'
import { prefersReducedMotion } from '@/hooks/useMediaQuery'
import { useUiStore } from '@/stores/uiStore'

export type MessageScrollerHandle = {
  /** Re-attach to the bottom (after sending) and scroll there. */
  stickToBottom: (smooth?: boolean) => void
}

const STICK_THRESHOLD = 24
const JUMP_THRESHOLD = 240
/** How quickly the view catches up with new lines (ms to cover ~63% of the distance). */
const FOLLOW_MS = 90

/**
 * Scroll container that follows new content while the user is at the bottom.
 * Growth is detected with a ResizeObserver, so streaming never re-renders this
 * component. Scrolling up detaches and reveals a "Jump to latest" button.
 */
export const MessageScroller = forwardRef<MessageScrollerHandle, { children: ReactNode; resetKey: string }>(
  function MessageScroller({ children, resetKey }, ref) {
    const containerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const stick = useRef(true)
    const lastTop = useRef(0)
    const followFrame = useRef(0)
    const [showJump, setShowJump] = useState(false)

    /**
     * Glide to the bottom, one animation frame at a time. Streaming adds a line every
     * few hundred ms; jumping straight there makes the text judder upwards, and
     * repeated native smooth-scrolls fight each other.
     */
    const follow = useCallback(() => {
      if (followFrame.current) return
      let last = performance.now()
      const step = (now: number) => {
        const el = containerRef.current
        const goal = el ? el.scrollHeight - el.clientHeight : 0
        if (!el || !stick.current || goal - el.scrollTop <= 1) {
          if (el && stick.current) el.scrollTop = goal
          followFrame.current = 0
          return
        }
        const k = 1 - Math.exp(-(now - last) / FOLLOW_MS)
        last = now
        el.scrollTop += Math.max(1, (goal - el.scrollTop) * k)
        followFrame.current = requestAnimationFrame(step)
      }
      followFrame.current = requestAnimationFrame(step)
    }, [])

    useEffect(() => () => cancelAnimationFrame(followFrame.current), [])

    const scrollToBottom = useCallback(
      (smooth: boolean) => {
        const el = containerRef.current
        if (!el) return
        if (smooth && !prefersReducedMotion()) follow()
        else el.scrollTop = el.scrollHeight
      },
      [follow],
    )

    useImperativeHandle(
      ref,
      () => ({
        stickToBottom: (smooth = true) => {
          stick.current = true
          setShowJump(false)
          scrollToBottom(smooth)
        },
      }),
      [scrollToBottom],
    )

    // New conversation: start at the bottom.
    useEffect(() => {
      stick.current = true
      setShowJump(false)
      scrollToBottom(false)
      useUiStore.getState().setContentScrolled((containerRef.current?.scrollTop ?? 0) > 4)
      return () => useUiStore.getState().setContentScrolled(false)
    }, [resetKey, scrollToBottom])

    useEffect(() => {
      const content = contentRef.current
      if (!content) return
      const observer = new ResizeObserver(() => {
        const el = containerRef.current
        if (!el || !stick.current) return
        // Glide over a few new lines; jump when a whole conversation has just loaded.
        const distance = el.scrollHeight - el.clientHeight - el.scrollTop
        scrollToBottom(distance < el.clientHeight * 0.6)
      })
      observer.observe(content)
      return () => observer.disconnect()
    }, [scrollToBottom])

    const onScroll = () => {
      const el = containerRef.current
      if (!el) return
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight
      if (el.scrollTop < lastTop.current - 2 && distance > STICK_THRESHOLD) stick.current = false
      else if (distance <= STICK_THRESHOLD) stick.current = true
      lastTop.current = el.scrollTop
      useUiStore.getState().setContentScrolled(el.scrollTop > 4)
      const jump = distance > JUMP_THRESHOLD
      setShowJump((prev) => (prev === jump ? prev : jump))
    }

    return (
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div ref={containerRef} onScroll={onScroll} className="min-h-0 flex-1 overflow-y-auto [overflow-anchor:none]">
          <div ref={contentRef}>{children}</div>
        </div>
        <AnimatePresence>
          {showJump && (
            <motion.button
              type="button"
              initial={{ opacity: 0, y: 12, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.9 }}
              whileTap={{ scale: 0.94 }}
              transition={spring.snappy}
              onClick={() => {
                stick.current = true
                setShowJump(false)
                scrollToBottom(true)
              }}
              className="glass absolute bottom-4 left-1/2 -ml-[62px] inline-flex h-9 w-[124px] items-center justify-center gap-1.5 rounded-full text-xs font-medium text-ink shadow-popover"
            >
              <ArrowDown className="size-3.5" aria-hidden />
              Jump to latest
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    )
  },
)
